import { useQueryState } from "nuqs";
import { z } from "zod";
import { useMemo } from "react";
import { serializeEntityFilters, parseEntityFilters } from "../lib/filter-codec-utils";
import { FilterOperators as FilterOperatorsTypes } from "@okeano-gmbh/pylo-utils";
import { FILTER_OPERATOR_META } from "../lib/entity-filter-operators";
import type { LucideIcon } from "lucide-react";
import {
  Search,
  SearchX,
  Equal,
  EqualNot,
  TextCursorInput,
  ChevronRight,
  ChevronLeft,
  ChevronsRight,
  ChevronsLeft,
  CircleMinus,
  CirclePlus,
  CircleCheck,
  CircleX,
  Braces,
  List,
  ListX,
} from "lucide-react";

export const FilterOperators = z.enum(FilterOperatorsTypes);

export interface FilterOperatorOption {
  value: FilterOperatorsType;
  label: string;
  needsInput: boolean;
  activeFor?: Array<string>;
  icon?: LucideIcon;
  iconClassName?: string;
}

/** Operators that support a case sensitivity toggle. */
const CASE_TOGGLEABLE_OPERATORS = new Set<string>(["ilike", "notiLike", "startsWith", "endsWith"]);

export const hasCaseSensitivityToggle = (operator: string): boolean =>
  CASE_TOGGLEABLE_OPERATORS.has(operator);

// isNull/isNotNull are omitted from entity-filter-operators.ts (and so hidden here
// too) — most non-technical users don't understand the difference between null and
// empty. "is empty" / "is not empty" covers the common use case and is more intuitive.
const OPERATOR_LABELS_AND_ICONS: Record<
  string,
  { label: string; icon?: LucideIcon; iconClassName?: string }
> = {
  ilike: { label: "includes", icon: Search },
  notiLike: { label: "does not include", icon: SearchX },
  equal: { label: "equal", icon: Equal },
  notEqual: { label: "not equal", icon: EqualNot },
  startsWith: { label: "starts with", icon: TextCursorInput },
  endsWith: { label: "ends with", icon: TextCursorInput, iconClassName: "rotate-180" },
  // JSON/RICHTEXT are excluded from in/notIn: their values routinely contain commas,
  // which is the chip/storage delimiter, so list matching would silently mangle them.
  in: { label: "is any of", icon: List },
  notIn: { label: "is none of", icon: ListX },
  greaterThan: { label: "greater than", icon: ChevronRight },
  lessThan: { label: "less than", icon: ChevronLeft },
  greaterThanOrEqual: { label: "greater than or equal", icon: ChevronsRight },
  lessThanOrEqual: { label: "less than or equal", icon: ChevronsLeft },
  isEmpty: { label: "is empty", icon: CircleMinus },
  isNotEmpty: { label: "is not empty", icon: CirclePlus },
  isTrue: { label: "is true", icon: CircleCheck },
  isFalse: { label: "is false", icon: CircleX },
  contains: { label: "contains", icon: Braces },
};

export const availableFilterOperators: Array<FilterOperatorOption> = FILTER_OPERATOR_META.map(
  (meta) => ({
    ...meta,
    ...OPERATOR_LABELS_AND_ICONS[meta.value],
  }),
) as Array<FilterOperatorOption>;

export type FilterOperatorsType = z.infer<typeof FilterOperators>;

/** Pre-selected operator for a newly added filter on `fieldName` (may be a path like "customer.id"). */
export const getDefaultFilterOperator = (
  fieldName: string,
  fieldType?: string,
): FilterOperatorsType => {
  // `id` is stored as a TEXT field, which would otherwise default to "includes".
  if (fieldName === "id" || fieldName.endsWith(".id")) return "equal";
  if (!fieldType || fieldType === "FILTER-CUSTOM-FIELD") return availableFilterOperators[0].value;
  const normalized = fieldType.toUpperCase();
  return (
    availableFilterOperators.find((x) => x.activeFor?.includes(normalized)) ??
    availableFilterOperators[0]
  ).value;
};

/**
 * Check if an operator requires a value input.
 * Uses the needsInput property from availableFilterOperators as the single source of truth.
 */
export const operatorNeedsValue = (operator: string): boolean => {
  const operatorConfig = availableFilterOperators.find((op) => op.value === operator);
  // Default to true if operator not found (safer default)
  return operatorConfig?.needsInput ?? true;
};

export const FilterSchema = z.object({
  field: z.string(),
  fieldType: z.string(),
  operator: FilterOperators,
  value: z.string(),
  active: z.boolean(),
  id: z.string(),
  caseSensitive: z.boolean().optional(),
});

export type Filter = z.infer<typeof FilterSchema>;

/**
 * if you experience infinite rerendering in your component,
 * call setFilter after first render.
 */
export const useFilter = () => {
  const [filter, setFilter] = useQueryState<Filter[]>("filter", {
    parse: (s: string) => {
      try {
        const arr = parseEntityFilters(s) as Filter[];
        // Optional: validate against your zod schema
        const res = z.array(FilterSchema).safeParse(arr);
        return res.success ? res.data : [];
      } catch {
        return [];
      }
    },
    serialize: (value: Filter[]) => {
      try {
        return serializeEntityFilters(value);
      } catch {
        return "";
      }
    },
    defaultValue: [],
    shallow: true,
  });

  const memoizedFilter = useMemo(() => filter, [filter]);
  return [memoizedFilter, setFilter] as const;
};

export const parseFilter = (filter: {
  field: string;
  operator: FilterOperatorsType;
  value: string;
  fieldType: string;
  caseSensitive?: boolean;
}): {
  field: string;
  operator: FilterOperatorsType;
  value?: string;
  values?: Array<string>;
} => {
  const sensitive = filter.caseSensitive ?? false;

  if (filter.operator === "startsWith") {
    return {
      field: filter.field,
      operator: sensitive ? "like" : "ilike",
      value: filter.value + "%",
    };
  }
  if (filter.operator === "endsWith") {
    return {
      field: filter.field,
      operator: sensitive ? "like" : "ilike",
      value: "%" + filter.value,
    };
  }
  if (filter.operator === "ilike") {
    return {
      field: filter.field,
      operator: sensitive ? "like" : "ilike",
      value: "%" + filter.value + "%",
    };
  }
  if (filter.operator === "notiLike") {
    return {
      field: filter.field,
      operator: sensitive ? "notLike" : "notiLike",
      value: "%" + filter.value + "%",
    };
  }
  if (filter.operator === "in" || filter.operator === "notIn") {
    // Relation filters store related-entity ids and must match on `<relation>.id`;
    // scalar fields (STRING, ID, INT, ...) match on the field itself. Relation field
    // types are GraphQL object types ending in "Data"/"ListData".
    const isRelation = filter.fieldType.endsWith("Data");
    return {
      field: isRelation ? `${filter.field}.id` : filter.field,
      operator: filter.operator,
      values: filter.value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean),
    };
  }
  return {
    field: filter.field,
    operator: filter.operator,
    value: filter.value,
  };
};
